import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Header from './Header';
import Footer from './Footer';
import { FlaskConical, Dna, Beaker, Target, BookOpen, Sparkles, Shield, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const PeptidesInfoPage = () => {
  const { currentLanguage, t } = useLanguage();

  const content = {
    en: {
      title: "What Are Peptides?",
      subtitle: "Understanding the building blocks of biological research",
      intro: "Peptides are short chains of amino acids linked by peptide bonds. They are fundamental to biological research, serving as signaling molecules and playing crucial roles in various physiological processes.",
      sections: [
        {
          icon: "dna",
          title: "Structure & Formation",
          content: "Peptides consist of 2-50 amino acids joined together by peptide bonds formed through a dehydration synthesis reaction. The sequence and length of amino acids determine each peptide's unique properties and biological activity."
        },
        {
          icon: "beaker",
          title: "Research Applications",
          content: "In research settings, peptides are used to study cell signaling, hormone function, immune responses, and metabolic pathways. They serve as valuable tools for understanding complex biological mechanisms and developing new research protocols."
        },
        {
          icon: "target",
          title: "Types of Research Peptides",
          content: "Research peptides include growth hormone releasing peptides (GHRPs), melanocyte-stimulating hormone analogs, and various signaling peptides. Each type has specific research applications in studying different biological systems."
        },
        {
          icon: "flask",
          title: "Quality & Purity",
          content: "High-quality research peptides require >99% purity verified through HPLC analysis. Proper synthesis, purification, and storage are essential to maintain peptide integrity for reliable research results."
        }
      ],
      sarmsTitle: "What Are SARMs?",
      sarmsContent: "Selective Androgen Receptor Modulators (SARMs) are a class of compounds that selectively bind to androgen receptors in specific tissues. They are extensively studied for their tissue-selective properties in research applications.",
      sarmsFeatures: [
        "Tissue-selective binding properties",
        "Used in muscle and bone research",
        "Non-steroidal structure",
        "Extensively documented in scientific literature"
      ],
      disclaimer: "All products are for research purposes only. Not for human consumption.",
      learnMore: "Our research peptides and SARMs are provided with full documentation and certificates of analysis. For research inquiries, please contact our scientific support team.",
      exploreProducts: "Explore Our Products"
    },
    es: {
      title: "¿Qué Son Los Péptidos?",
      subtitle: "Entendiendo los bloques de construcción de la investigación biológica",
      intro: "Los péptidos son cadenas cortas de aminoácidos unidos por enlaces peptídicos. Son fundamentales para la investigación biológica, sirviendo como moléculas de señalización y desempeñando roles cruciales en varios procesos fisiológicos.",
      sections: [
        {
          icon: "dna",
          title: "Estructura y Formación",
          content: "Los péptidos consisten en 2-50 aminoácidos unidos por enlaces peptídicos formados a través de una reacción de síntesis de deshidratación. La secuencia y longitud de los aminoácidos determinan las propiedades únicas y la actividad biológica de cada péptido."
        },
        {
          icon: "beaker",
          title: "Aplicaciones de Investigación",
          content: "En entornos de investigación, los péptidos se utilizan para estudiar señalización celular, función hormonal, respuestas inmunes y vías metabólicas. Sirven como herramientas valiosas para comprender mecanismos biológicos complejos y desarrollar nuevos protocolos de investigación."
        },
        {
          icon: "target",
          title: "Tipos de Péptidos de Investigación",
          content: "Los péptidos de investigación incluyen péptidos liberadores de hormona de crecimiento (GHRPs), análogos de hormona estimulante de melanocitos y varios péptidos de señalización. Cada tipo tiene aplicaciones de investigación específicas en el estudio de diferentes sistemas biológicos."
        },
        {
          icon: "flask",
          title: "Calidad y Pureza",
          content: "Los péptidos de investigación de alta calidad requieren >99% de pureza verificada mediante análisis HPLC. La síntesis, purificación y almacenamiento adecuados son esenciales para mantener la integridad del péptido y obtener resultados de investigación confiables."
        }
      ],
      sarmsTitle: "¿Qué Son Los SARMs?",
      sarmsContent: "Los Moduladores Selectivos del Receptor de Andrógenos (SARMs) son una clase de compuestos que se unen selectivamente a los receptores de andrógenos en tejidos específicos. Se estudian extensivamente por sus propiedades selectivas de tejido en aplicaciones de investigación.",
      sarmsFeatures: [
        "Propiedades de unión selectivas a tejidos",
        "Utilizados en investigación muscular y ósea",
        "Estructura no esteroidal",
        "Ampliamente documentados en literatura científica"
      ],
      disclaimer: "Todos los productos son solo para fines de investigación. No para consumo humano.",
      learnMore: "Nuestros péptidos de investigación y SARMs se proporcionan con documentación completa y certificados de análisis. Para consultas de investigación, por favor contacte a nuestro equipo de soporte científico.",
      exploreProducts: "Explorar Productos"
    },
    ru: {
      title: "Что такое пептиды?",
      subtitle: "Основы биологических исследований",
      intro: "Пептиды, это короткие цепочки аминокислот, соединённые пептидными связями. Они играют ключевую роль в биологических исследованиях, выступая сигнальными молекулами и участвуя в различных физиологических процессах.",
      sections: [
        {
          icon: "dna",
          title: "Структура и образование",
          content: "Пептиды состоят из 2-50 аминокислот, соединённых пептидными связями в результате реакции дегидратации. Последовательность и длина аминокислот определяют уникальные свойства и биологическую активность каждого пептида."
        },
        {
          icon: "beaker",
          title: "Применение в исследованиях",
          content: "В научных исследованиях пептиды используются для изучения клеточной сигнализации, гормональной функции, иммунных реакций и метаболических путей. Они служат ценными инструментами для понимания сложных биологических механизмов."
        },
        {
          icon: "target",
          title: "Типы исследовательских пептидов",
          content: "К исследовательским пептидам относятся пептиды, высвобождающие гормон роста (GHRP), аналоги меланоцитстимулирующего гормона и различные сигнальные пептиды. Каждый тип имеет свои области применения в изучении различных биологических систем."
        },
        {
          icon: "flask",
          title: "Качество и чистота",
          content: "Высококачественные исследовательские пептиды должны иметь чистоту более 99%, подтверждённую методом ВЭЖХ. Правильный синтез, очистка и хранение необходимы для сохранения целостности пептида и получения достоверных результатов."
        }
      ],
      sarmsTitle: "Что такое SARM?",
      sarmsContent: "Селективные модуляторы андрогенных рецепторов (SARM), это класс соединений, избирательно связывающихся с андрогенными рецепторами в определённых тканях. Они активно исследуются благодаря своим тканеселективным свойствам.",
      sarmsFeatures: [
        "Тканеселективное связывание",
        "Применяются в исследованиях мышц и костей",
        "Нестероидная структура",
        "Подробно описаны в научной литературе"
      ],
      disclaimer: "Вся продукция предназначена исключительно для научных исследований. Не подлежит употреблению человеком.",
      learnMore: "Наши исследовательские пептиды и SARM поставляются с полной документацией и сертификатами анализа. По вопросам исследований обращайтесь в нашу научную службу поддержки.",
      exploreProducts: "Перейти к каталогу"
    },
    de: {
      title: "Was sind Peptide?",
      subtitle: "Verstehen der Bausteine der biologischen Forschung",
      intro: "Peptide sind kurze Ketten von Aminosäuren, die durch Peptidbindungen verknüpft sind. Sie sind grundlegend für die biologische Forschung, dienen als Signalmoleküle und spielen eine entscheidende Rolle in verschiedenen physiologischen Prozessen.",
      sections: [
        {
          icon: "dna",
          title: "Struktur & Bildung",
          content: "Peptide bestehen aus 2-50 Aminosäuren, die durch Peptidbindungen verbunden sind, die durch eine Dehydratisierungs-Synthesereaktion gebildet werden. Die Sequenz und Länge der Aminosäuren bestimmen die einzigartigen Eigenschaften und die biologische Aktivität eines jeden Peptids."
        },
        {
          icon: "beaker",
          title: "Forschungsanwendungen",
          content: "In Forschungsumgebungen werden Peptide verwendet, um die Zellsignalübertragung, Hormonfunktionen, Immunantworten und Stoffwechselwege zu untersuchen. Sie dienen als wertvolle Werkzeuge zum Verständnis komplexer biologischer Mechanismen und zur Entwicklung neuer Forschungsprotokolle."
        },
        {
          icon: "target",
          title: "Arten von Forschungspeptiden",
          content: "Forschungspeptide umfassen Wachstumshormon-freisetzende Peptide (GHRPs), Analoga des Melanozyten-stimulierenden Hormons und verschiedene Signalmolekül-Peptide. Jeder Typ hat spezifische Forschungsanwendungen zur Untersuchung unterschiedlicher biologischer Systeme."
        },
        {
          icon: "flask",
          title: "Qualität & Reinheit",
          content: "Hochwertige Forschungspeptide erfordern eine Reinheit von >99 %, die durch HPLC-Analyse verifiziert wird. Eine sachgemäße Synthese, Reinigung und Lagerung sind unerlässlich, um die Peptidintegrität für zuverlässige Forschungsergebnisse zu erhalten."
        }
      ],
      sarmsTitle: "Was sind SARMs?",
      sarmsContent: "Selektive Androgenrezeptor-Modulatoren (SARMs) sind eine Klasse von Verbindungen, die selektiv an Androgenrezeptoren in bestimmten Geweben binden. Sie werden intensiv auf ihre gewebespezifischen Eigenschaften in Forschungsanwendungen untersucht.",
      sarmsFeatures: [
        "Gewebespezifische Bindungseigenschaften",
        "Einsatz in Muskel- und Knochenforschung",
        "Nicht-steroidale Struktur",
        "Umfassend in der wissenschaftlichen Literatur dokumentiert"
      ],
      disclaimer: "Alle Produkte sind ausschließlich für Forschungszwecke bestimmt. Nicht für den menschlichen Verzehr.",
      learnMore: "Unsere Forschungspeptide und SARMs werden mit vollständiger Dokumentation und Analysenzertifikaten bereitgestellt. Für Forschungsanfragen wenden Sie sich bitte an unser wissenschaftliches Supportteam.",
      exploreProducts: "Unsere Produkte entdecken"
    },
    fr: {
      title: "Que sont les peptides ?",
      subtitle: "Comprendre les éléments constitutifs de la recherche biologique",
      intro: "Les peptides sont de courtes chaînes d'acides aminés reliés par des liaisons peptidiques. Ils sont fondamentaux pour la recherche biologique, servant de molécules de signalisation et jouant un rôle crucial dans divers processus physiologiques.",
      sections: [
        {
          icon: "dna",
          title: "Structure et formation",
          content: "Les peptides sont constitués de 2 à 50 acides aminés reliés entre eux par des liaisons peptidiques formées par une réaction de synthèse par déshydratation. La séquence et la longueur des acides aminés déterminent les propriétés uniques et l'activité biologique de chaque peptide."
        },
        {
          icon: "beaker",
          title: "Applications en recherche",
          content: "Dans les environnements de recherche, les peptides sont utilisés pour étudier la signalisation cellulaire, la fonction hormonale, les réponses immunitaires et les voies métaboliques. Ils servent d’outils précieux pour comprendre des mécanismes biologiques complexes et développer de nouveaux protocoles de recherche."
        },
        {
          icon: "target",
          title: "Types de peptides de recherche",
          content: "Les peptides de recherche incluent les peptides de libération de l’hormone de croissance (GHRP), les analogues de l’hormone stimulant les mélanocytes et divers peptides de signalisation. Chaque type possède des applications de recherche spécifiques pour l’étude de différents systèmes biologiques."
        },
        {
          icon: "flask",
          title: "Qualité et pureté",
          content: "Des peptides de recherche de haute qualité nécessitent une pureté >99 % vérifiée par analyse HPLC. Une synthèse, une purification et un stockage appropriés sont essentiels pour maintenir l’intégrité des peptides et obtenir des résultats de recherche fiables."
        }
      ],
      sarmsTitle: "Que sont les SARMs ?",
      sarmsContent: "Les modulateurs sélectifs des récepteurs aux androgènes (Selective Androgen Receptor Modulators, SARMs) sont une classe de composés qui se lient de manière sélective aux récepteurs aux androgènes dans des tissus spécifiques. Ils sont largement étudiés pour leurs propriétés tissulaires sélectives dans des applications de recherche.",
      sarmsFeatures: [
        "Propriétés de liaison sélective aux tissus",
        "Utilisés dans la recherche sur les muscles et les os",
        "Structure non stéroïdienne",
        "Amplement documentés dans la littérature scientifique"
      ],
      disclaimer: "Tous les produits sont uniquement destinés à des fins de recherche. Ne pas utiliser pour la consommation humaine.",
      learnMore: "Nos peptides de recherche et SARMs sont fournis avec une documentation complète et des certificats d’analyse. Pour toute demande liée à la recherche, veuillez contacter notre équipe d’assistance scientifique.",
      exploreProducts: "Découvrir nos produits"
    },
    it: {
      title: "Cosa sono i peptidi?",
      subtitle: "Comprendere i mattoni fondamentali della ricerca biologica",
      intro: "I peptidi sono brevi catene di amminoacidi collegati da legami peptidici. Sono fondamentali per la ricerca biologica, fungendo da molecole di segnalazione e svolgendo ruoli cruciali in vari processi fisiologici.",
      sections: [
        {
          icon: "dna",
          title: "Struttura e formazione",
          content: "I peptidi sono costituiti da 2 a 50 amminoacidi uniti tra loro da legami peptidici formati attraverso una reazione di sintesi per disidratazione. La sequenza e la lunghezza degli amminoacidi determinano le proprietà uniche e l'attività biologica di ciascun peptide."
        },
        {
          icon: "beaker",
          title: "Applicazioni nella ricerca",
          content: "In ambito di ricerca, i peptidi vengono utilizzati per studiare la segnalazione cellulare, la funzione ormonale, le risposte immunitarie e le vie metaboliche. Rappresentano strumenti preziosi per comprendere complessi meccanismi biologici e sviluppare nuovi protocolli di ricerca."
        },
        {
          icon: "target",
          title: "Tipi di peptidi di ricerca",
          content: "I peptidi di ricerca includono i peptidi di rilascio dell'ormone della crescita (GHRP), gli analoghi dell'ormone melanocita-stimolante e vari peptidi di segnalazione. Ogni tipo ha specifiche applicazioni di ricerca nello studio di diversi sistemi biologici."
        },
        {
          icon: "flask",
          title: "Qualità e purezza",
          content: "I peptidi di ricerca di alta qualità richiedono una purezza >99% verificata tramite analisi HPLC. Una corretta sintesi, purificazione e conservazione sono essenziali per mantenere l'integrità del peptide e ottenere risultati di ricerca affidabili."
        }
      ],
      sarmsTitle: "Cosa sono i SARM?",
      sarmsContent: "I Modulatori Selettivi del Recettore degli Androgeni (SARM) sono una classe di composti che si legano selettivamente ai recettori degli androgeni in tessuti specifici. Sono ampiamente studiati per le loro proprietà tessuto-selettive nelle applicazioni di ricerca.",
      sarmsFeatures: [
        "Proprietà di legame tessuto-selettive",
        "Utilizzati nella ricerca su muscolo e osso",
        "Struttura non steroidea",
        "Ampiamente documentati nella letteratura scientifica"
      ],
      disclaimer: "Tutti i prodotti sono destinati esclusivamente alla ricerca. Non destinati al consumo umano.",
      learnMore: "I nostri peptidi di ricerca e SARM sono forniti con documentazione completa e certificati di analisi. Per richieste di ricerca, contatta il nostro team di supporto scientifico.",
      exploreProducts: "Esplora i nostri prodotti"
    },
    pl: {
      title: "Czym są peptydy?",
      subtitle: "Zrozumienie podstawowych elementów badań biologicznych",
      intro: "Peptydy to krótkie łańcuchy aminokwasów połączonych wiązaniami peptydowymi. Są fundamentalne dla badań biologicznych, pełniąc funkcję cząsteczek sygnałowych i odgrywając kluczową rolę w różnych procesach fizjologicznych.",
      sections: [
        {
          icon: "dna",
          title: "Struktura i powstawanie",
          content: "Peptydy składają się z 2-50 aminokwasów połączonych wiązaniami peptydowymi powstającymi w reakcji kondensacji (syntezy odwodnieniowej). Sekwencja i długość aminokwasów determinują unikalne właściwości każdego peptydu oraz jego aktywność biologiczną."
        },
        {
          icon: "beaker",
          title: "Zastosowania badawcze",
          content: "W warunkach badawczych peptydy są wykorzystywane do badania sygnalizacji komórkowej, funkcji hormonów, odpowiedzi immunologicznych oraz szlaków metabolicznych. Służą jako cenne narzędzia do zrozumienia złożonych mechanizmów biologicznych i opracowywania nowych protokołów badawczych."
        },
        {
          icon: "target",
          title: "Rodzaje peptydów badawczych",
          content: "Peptydy badawcze obejmują peptydy uwalniające hormon wzrostu (GHRP), analogi hormonu stymulującego melanocyty oraz różne peptydy sygnałowe. Każdy typ ma specyficzne zastosowania badawcze w analizie różnych układów biologicznych."
        },
        {
          icon: "flask",
          title: "Jakość i czystość",
          content: "Wysokiej jakości peptydy badawcze wymagają czystości >99% potwierdzonej analizą HPLC. Odpowiednia synteza, oczyszczanie i przechowywanie są kluczowe dla zachowania integralności peptydu i uzyskiwania wiarygodnych wyników badań."
        }
      ],
      sarmsTitle: "Czym są SARM?",
      sarmsContent: "Selektywne modulatory receptora androgenowego (Selective Androgen Receptor Modulators, SARMs) to klasa związków, które wybiórczo wiążą się z receptorami androgenowymi w określonych tkankach. Są intensywnie badane pod kątem swoich tkankowo-selektywnych właściwości w zastosowaniach badawczych.",
      sarmsFeatures: [
        "Tkankowo-selektywne właściwości wiązania",
        "Wykorzystywane w badaniach mięśni i kości",
        "Niesteroidowa struktura",
        "Szeroko udokumentowane w literaturze naukowej"
      ],
      disclaimer: "Wszystkie produkty są przeznaczone wyłącznie do celów badawczych. Nie do spożycia przez ludzi.",
      learnMore: "Nasze peptydy badawcze i SARM są dostarczane z pełną dokumentacją i certyfikatami analizy. W sprawie zapytań badawczych prosimy o kontakt z naszym zespołem wsparcia naukowego.",
      exploreProducts: "Poznaj nasze produkty"
    },
    nl: {
      title: "Wat Zijn Peptiden?",
      subtitle: "Inzicht in de bouwstenen van biologisch onderzoek",
      intro: "Peptiden zijn korte ketens van aminozuren die door peptidebindingen met elkaar verbonden zijn. Ze zijn fundamenteel voor biologisch onderzoek, dienen als signaalmoleculen en spelen cruciale rollen in verschillende fysiologische processen.",
      sections: [
        {
          icon: "dna",
          title: "Structuur & Vorming",
          content: "Peptiden bestaan uit 2-50 aminozuren die met elkaar verbonden zijn door peptidebindingen die worden gevormd via een condensatiereactie (dehydratiesynthese). De volgorde en lengte van aminozuren bepalen de unieke eigenschappen en biologische activiteit van elke peptide."
        },
        {
          icon: "beaker",
          title: "Onderzoekstoepassingen",
          content: "In onderzoeksomgevingen worden peptiden gebruikt om celsignalering, hormoonfunctie, immuunresponsen en metabole routes te bestuderen. Ze dienen als waardevolle hulpmiddelen voor het begrijpen van complexe biologische mechanismen en het ontwikkelen van nieuwe onderzoeksprotocollen."
        },
        {
          icon: "target",
          title: "Typen Onderzoekspeptiden",
          content: "Onderzoekspeptiden omvatten groeihormoon-afgevende peptiden (GHRP’s), melanocyt-stimulerende hormoonanalogen en diverse signaalpeptiden. Elk type heeft specifieke onderzoekstoepassingen bij het bestuderen van verschillende biologische systemen."
        },
        {
          icon: "flask",
          title: "Kwaliteit & Zuiverheid",
          content: "Onderzoekspeptiden van hoge kwaliteit vereisen >99% zuiverheid, geverifieerd via HPLC-analyse. Juiste synthese, zuivering en opslag zijn essentieel om de integriteit van peptiden te behouden voor betrouwbare onderzoeksresultaten."
        }
      ],
      sarmsTitle: "Wat Zijn SARMs?",
      sarmsContent: "Selectieve Androgeen Receptormodulatoren (SARMs) zijn een klasse verbindingen die selectief binden aan androgeenreceptoren in specifieke weefsels. Ze worden uitgebreid bestudeerd vanwege hun weefsel-specifieke eigenschappen in onderzoekstoepassingen.",
      sarmsFeatures: [
        "Weefsel-specifieke bindings­eigenschappen",
        "Gebruikt in spier- en botonderzoek",
        "Niet-steroïdale structuur",
        "Uitgebreid gedocumenteerd in de wetenschappelijke literatuur"
      ],
      disclaimer: "Alle producten zijn uitsluitend voor onderzoeksdoeleinden. Niet voor menselijke consumptie.",
      learnMore: "Onze onderzoekspeptiden en SARMs worden geleverd met volledige documentatie en analysecertificaten. Voor onderzoeksaanvragen kunt u contact opnemen met ons wetenschappelijk ondersteuningsteam.",
      exploreProducts: "Ontdek Onze Producten"
    },
    pt: {
      title: "O que são peptídeos?",
      subtitle: "Compreendendo os blocos de construção da pesquisa biológica",
      intro: "Peptídeos são cadeias curtas de aminoácidos ligados por ligações peptídicas. Eles são fundamentais para a pesquisa biológica, atuando como moléculas sinalizadoras e desempenhando papéis cruciais em vários processos fisiológicos.",
      sections: [
        {
          icon: "dna",
          title: "Estrutura e formação",
          content: "Peptídeos consistem em 2-50 aminoácidos unidos por ligações peptídicas formadas por meio de uma reação de síntese por desidratação. A sequência e o comprimento dos aminoácidos determinam as propriedades únicas e a atividade biológica de cada peptídeo."
        },
        {
          icon: "beaker",
          title: "Aplicações em pesquisa",
          content: "Em ambientes de pesquisa, peptídeos são usados para estudar sinalização celular, função hormonal, respostas imunológicas e vias metabólicas. Eles servem como ferramentas valiosas para compreender mecanismos biológicos complexos e desenvolver novos protocolos de pesquisa."
        },
        {
          icon: "target",
          title: "Tipos de peptídeos de pesquisa",
          content: "Peptídeos de pesquisa incluem peptídeos liberadores de hormônio do crescimento (GHRPs), análogos do hormônio estimulador de melanócitos e diversos peptídeos sinalizadores. Cada tipo possui aplicações específicas de pesquisa para o estudo de diferentes sistemas biológicos."
        },
        {
          icon: "flask",
          title: "Qualidade e pureza",
          content: "Peptídeos de pesquisa de alta qualidade requerem pureza >99% verificada por análise de HPLC. Síntese, purificação e armazenamento adequados são essenciais para manter a integridade do peptídeo e garantir resultados confiáveis em pesquisa."
        }
      ],
      sarmsTitle: "O que são SARMs?",
      sarmsContent: "Moduladores Seletivos do Receptor Androgênico (SARMs) são uma classe de compostos que se ligam seletivamente a receptores androgênicos em tecidos específicos. Eles são amplamente estudados por suas propriedades de seletividade tecidual em aplicações de pesquisa.",
      sarmsFeatures: [
        "Propriedades de ligação seletiva ao tecido",
        "Usados em pesquisa de músculo e osso",
        "Estrutura não esteroidal",
        "Amplamente documentados na literatura científica"
      ],
      disclaimer: "Todos os produtos são apenas para fins de pesquisa. Não são destinados ao consumo humano.",
      learnMore: "Nossos peptídeos de pesquisa e SARMs são fornecidos com documentação completa e certificados de análise. Para dúvidas relacionadas à pesquisa, entre em contato com nossa equipe de suporte científico.",
      exploreProducts: "Explore nossos produtos"
    },
  };

  const c = content[currentLanguage] || content.en;

  const getIcon = (iconName) => {
    switch (iconName) {
      case 'dna': return <Dna className="w-8 h-8 text-white" />;
      case 'beaker': return <Beaker className="w-8 h-8 text-white" />;
      case 'target': return <Target className="w-8 h-8 text-white" />;
      case 'flask': return <FlaskConical className="w-8 h-8 text-white" />;
      default: return <BookOpen className="w-8 h-8 text-white" />;
    }
  };

  const iconColors = [
    'from-capsule-teal to-capsule-aqua',
    'from-green-500 to-emerald-500',
    'from-capsule-coral to-capsule-salmon',
    'from-purple-500 to-violet-500'
  ];

  return (
    <div className="min-h-screen bg-capsule-bg">
      <Header />

      {/* Hero Banner - Capsule 8 Design */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-capsule-teal-dark">
        {/* DNA Decoration */}
        <div className="absolute right-0 top-0 bottom-0 w-96 opacity-20">
          <svg viewBox="0 0 200 400" fill="none" className="w-full h-full">
            <defs>
              <linearGradient id="heroGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#14B8A6" />
                <stop offset="100%" stopColor="#22D3EE" />
              </linearGradient>
            </defs>
            <path d="M50 0 Q120 50 50 100 Q-20 150 50 200 Q120 250 50 300 Q-20 350 50 400" 
                  stroke="url(#heroGradient)" strokeWidth="3" fill="none"/>
            <path d="M150 0 Q80 50 150 100 Q220 150 150 200 Q80 250 150 300 Q220 350 150 400" 
                  stroke="url(#heroGradient)" strokeWidth="3" fill="none"/>
          </svg>
        </div>
        
        {/* Floating spheres */}
        <div className="absolute top-20 right-32 w-20 h-20 rounded-full bg-gradient-to-br from-capsule-rose-gold/30 to-capsule-salmon/20 blur-xl"></div>
        <div className="absolute bottom-20 right-48 w-16 h-16 rounded-full bg-gradient-to-br from-capsule-teal/30 to-capsule-aqua/20 blur-xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-20 md:py-28">
          <div className="max-w-3xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full mb-6 border border-white/20">
              <Dna className="w-4 h-4 text-capsule-aqua" />
              <span className="text-sm font-medium text-capsule-aqua">{t('peptides_scientific_research')}</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
              {c.title}
            </h1>
            <p className="text-xl md:text-2xl text-transparent bg-clip-text bg-gradient-to-r from-capsule-teal to-capsule-aqua mb-6">
              {c.subtitle}
            </p>
            <p className="text-gray-300 text-lg max-w-2xl leading-relaxed">
              {c.intro}
            </p>
          </div>
        </div>
        
        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 60L48 55C96 50 192 40 288 35C384 30 480 30 576 33.3C672 37 768 43 864 45C960 47 1056 45 1152 41.7C1248 38 1344 33 1392 30.8L1440 29V60H1392C1344 60 1248 60 1152 60C1056 60 960 60 864 60C768 60 672 60 576 60C480 60 384 60 288 60C192 60 96 60 48 60H0Z" fill="#E7EAED"/>
          </svg>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 gap-6">
          {c.sections.map((section, index) => (
            <div key={index} className="glass-card rounded-3xl p-8 hover:shadow-glass-hover transition-all duration-300">
              <div className={`w-14 h-14 bg-gradient-to-br ${iconColors[index]} rounded-2xl flex items-center justify-center mb-6 shadow-lg`}>
                {getIcon(section.icon)}
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">{section.title}</h2>
              <p className="text-gray-600 leading-relaxed">{section.content}</p>
            </div>
          ))}
        </div>
      </div>

      {/* SARMs Section */}
      <div className="bg-gradient-to-r from-capsule-teal/10 via-capsule-aqua/5 to-capsule-teal/10 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-capsule-teal text-sm font-semibold tracking-wider">{t('peptides_research_compounds')}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 mt-2">{c.sarmsTitle}</h2>
              <p className="text-gray-600 mb-6 leading-relaxed">{c.sarmsContent}</p>
              <ul className="space-y-3">
                {c.sarmsFeatures.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-capsule-teal to-capsule-aqua flex items-center justify-center">
                      <Sparkles className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="glass-card rounded-3xl p-4 shadow-soft-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_peptide-lab-1/artifacts/tvzc9oz0_WhatsApp%20Image%202026-02-07%20at%2013.29.48.jpeg"
                alt={t('peptides_sarms_research')}
                className="rounded-2xl w-full"
              />
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <Link 
            to="/products"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-capsule-teal to-capsule-aqua hover:from-capsule-teal-dark hover:to-capsule-teal text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 shadow-teal-glow hover:shadow-lg hover:scale-105"
          >
            {c.exploreProducts}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-capsule-teal" />
            <p className="text-capsule-aqua font-semibold">{c.disclaimer}</p>
          </div>
          <p className="text-gray-400 max-w-2xl mx-auto">{c.learnMore}</p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PeptidesInfoPage;
